import { Layer, SkeletonText, Stack, Toggle } from "@carbon/react";
import { ServerError } from "@flaminem/utils";
import { FC, useEffect, useState } from "react";

import { AlertDecisionTag } from "src/components/Alerts/AlertDecisionTag";
import FilterableMultiPicker from "src/components/Pickers/FilterableMultiPickerFormField.tsx";
import { TableToolbarFilter } from "src/components/Tables/TableToolbarFilter.tsx";
import { CaseType } from "src/graphql/anti-fraud/__generated__/graphql";
import { useAlertValidationDecisions } from "src/hooks/alerts/useAlertValidationDecisions";
import { useT } from "src/internationalization";
import { AlertValidationDecision } from "src/types/alert";
import { Rule } from "src/types/rule";

export type AlertsFilterFilterProps = {
  onApplyFilter: (values: FilterState) => void;
  onResetFilter: () => void;
  values: FilterState;
  rules: Rule[];
  error?: ServerError | null;
  loading: boolean;
  displayDecisionFilter: boolean;
  caseTypes: Pick<CaseType, "code" | "name">[];
};

type FilterState = {
  ruleIds: string[];
  decisions: AlertValidationDecision[];
  onlyUserAlerts: boolean;
  caseTypeCodes: string[];
};

export const AlertsFilter: FC<AlertsFilterFilterProps> = ({
  onApplyFilter,
  onResetFilter,
  values,
  loading,
  error,
  rules,
  caseTypes,
  displayDecisionFilter,
}) => {
  const [filterState, setFilterState] = useState<FilterState>({
    ruleIds: values.ruleIds,
    onlyUserAlerts: values.onlyUserAlerts,
    decisions: values.decisions,
    caseTypeCodes: [],
  });

  const decisions = useAlertValidationDecisions();

  const selectedRuleItems = rules?.filter((rule) => filterState.ruleIds.includes(rule.id)) ?? [];

  const selectedCaseTypeItems =
    caseTypes?.filter((caseType) => filterState.caseTypeCodes.includes(caseType.code)) ?? [];

  const selectedDecisionItems =
    decisions?.filter((decision) => filterState.decisions.includes(decision.id)) ?? [];
  const t = useT();

  useEffect(() => {
    setFilterState(values);
  }, [values]);

  return loading ? (
    <SkeletonText className="!h-9 !w-9 !mb-0" />
  ) : (
    <TableToolbarFilter
      onPopoverOpen={() =>
        setFilterState({
          ruleIds: values.ruleIds,
          onlyUserAlerts: values.onlyUserAlerts,
          decisions: values.decisions,
          caseTypeCodes: [],
        })
      }
      onApplyFilter={() => onApplyFilter(filterState)}
      onResetFilter={onResetFilter}
      onPopoverClose={() => {
        setFilterState({
          ruleIds: values.ruleIds,
          onlyUserAlerts: values.onlyUserAlerts,
          decisions: values.decisions,
          caseTypeCodes: [],
        });
      }}
      numberOfFiltersApplied={
        (values.ruleIds.length > 0 ? 1 : 0) +
        (values.onlyUserAlerts ? 1 : 0) +
        (values.decisions.length > 0 ? 1 : 0)
      }
    >
      <Layer className="w-[500px] pb-7 z-50" level={2}>
        <Stack gap={5}>
          <Toggle
            className="w-full"
            labelText={t("page.alerts.table.filters.form.user.title")}
            size="sm"
            labelA={t("page.alerts.table.filters.form.user.helper")}
            labelB={t("page.alerts.table.filters.form.user.helper")}
            toggled={filterState.onlyUserAlerts}
            id="onlyUserAlerts"
            onToggle={(value) => {
              setFilterState((prevState) => ({
                ...prevState,
                onlyUserAlerts: value,
              }));
            }}
          />
          <Layer level={1}>
            <FilterableMultiPicker
              id="rules"
              titleText={t("rules.title")}
              helperText={t("page.alerts.table.filters.form.rules.helper")}
              items={rules}
              selectedItems={selectedRuleItems}
              loading={loading}
              error={error}
              itemToString={(item) => item?.name ?? ""}
              onChange={({ selectedItems }) => {
                const ruleIds = selectedItems.map((item) => item.id);
                setFilterState((prevState) => ({
                  ...prevState,
                  ruleIds,
                }));
              }}
            />
          </Layer>
          <Layer level={1}>
            <FilterableMultiPicker
              id="caseTypes"
              titleText={t("page.alerts.table.filters.form.case.type.title")}
              items={caseTypes || []}
              selectedItems={selectedCaseTypeItems}
              loading={loading}
              error={error}
              itemToString={(item) => item?.name ?? ""}
              onChange={({ selectedItems }) => {
                const caseTypeCodes = selectedItems.map((item) => item.code);
                setFilterState((prevState) => ({
                  ...prevState,
                  caseTypeCodes,
                }));
              }}
            />
          </Layer>
          {displayDecisionFilter && (
            <Layer level={1}>
              <FilterableMultiPicker
                id="decisions"
                titleText={t("page.alerts.table.filters.form.decisions.title")}
                helperText={t("page.alerts.table.filters.form.decisions.helper")}
                items={decisions}
                selectedItems={selectedDecisionItems}
                loading={loading}
                error={error}
                itemToString={(item) => item?.title ?? ""}
                renderItem={(item) => <AlertDecisionTag decision={item.id} />}
                onChange={({ selectedItems }) => {
                  const decisions = selectedItems.map((item) => item.id);
                  setFilterState((prevState) => ({
                    ...prevState,
                    decisions,
                  }));
                }}
              />
            </Layer>
          )}
        </Stack>
      </Layer>
    </TableToolbarFilter>
  );
};
